/**
 * Client-side mirror of packages/shared/src/rbac/index.ts's permission matrix.
 * UI-gating ONLY — hides actions a role can't take so the app feels coherent.
 * The API independently re-checks every one of these via requirePermission()
 * on every route; nothing here is a security boundary.
 */

export type Role = 'owner' | 'admin' | 'operator' | 'viewer';

export type Action =
  | 'tenant:manage'
  | 'user:manage'
  | 'apiKey:manage'
  | 'target:read'
  | 'target:write'
  | 'assetGroup:read'
  | 'assetGroup:write'
  | 'engagement:read'
  | 'engagement:write'
  | 'scopePolicy:read'
  | 'scopePolicy:write'
  | 'authorization:read'
  | 'authorization:write'
  | 'authorization:activate'
  | 'preflight:run'
  | 'audit:read'
  | 'broker:read'
  | 'broker:manage'
  | 'internalAgent:read'
  | 'internalAgent:manage'
  | 'internalAgent:dispatch'
  | 'endpointAgent:read'
  | 'endpointAgent:manage'
  | 'finding:read'
  | 'finding:confirm'
  | 'run:read'
  | 'run:manage'
  | 'report:read'
  | 'report:generate'
  | 'githubIntegration:read'
  | 'githubIntegration:manage'
  | 'killSwitch:read'
  | 'killSwitch:manage'
  | 'exploitation:manage';

const VIEWER_ACTIONS: Action[] = [
  'target:read',
  'assetGroup:read',
  'engagement:read',
  'scopePolicy:read',
  'authorization:read',
  'audit:read',
  'broker:read',
  'finding:read',
  'run:read',
  'report:read',
  'githubIntegration:read',
];

const OPERATOR_ACTIONS: Action[] = [
  ...VIEWER_ACTIONS,
  'target:write',
  'assetGroup:write',
  'engagement:write',
  'scopePolicy:write',
  'authorization:write',
  'preflight:run',
  'run:manage',
  'report:generate',
  'internalAgent:read',
  // Mirrors packages/shared/src/rbac/index.ts's OPERATOR_ACTIONS exactly — dispatching a job to
  // an already-registered agent (e.g. Quick Scan) is lighter than internalAgent:manage.
  'internalAgent:dispatch',
  'endpointAgent:read',
];

const ADMIN_ACTIONS: Action[] = [
  ...OPERATOR_ACTIONS,
  'user:manage',
  'apiKey:manage',
  'authorization:activate',
  'broker:manage',
  'internalAgent:manage',
  'endpointAgent:manage',
  'githubIntegration:manage',
  'killSwitch:read',
  // Mirrors packages/shared/src/rbac/index.ts's ADMIN_ACTIONS — Owner+Admin only, gates the
  // Metasploit auxiliary-module "Attempt Exploit" card (FindingDetail.tsx).
  'exploitation:manage',
];

// BUI-1017: 'finding:confirm' (the "Confirm finding" button) is Owner-only, mirroring
// packages/shared/src/rbac/index.ts's OWNER_ACTIONS exactly.
const OWNER_ACTIONS: Action[] = [...ADMIN_ACTIONS, 'tenant:manage', 'killSwitch:manage', 'finding:confirm'];

const MATRIX: Record<Role, ReadonlySet<Action>> = {
  viewer: new Set(VIEWER_ACTIONS),
  operator: new Set(OPERATOR_ACTIONS),
  admin: new Set(ADMIN_ACTIONS),
  owner: new Set(OWNER_ACTIONS),
};

export function can(role: Role | undefined, action: Action): boolean {
  if (!role) return false;
  return MATRIX[role]?.has(action) ?? false;
}

export const ROLE_DESCRIPTIONS: Record<Role, string> = {
  owner: 'Full control, including workspace settings and managing Admins.',
  admin: 'Manages users, targets, and engagements; runs tests.',
  operator: 'Runs tests and manages targets/engagements, but not users.',
  viewer: 'Read-only access to findings, reports, and audit logs.',
};
